import type { MessageAttachment } from '@wix/ai-scheduling-assistant-common-lib/entities';
import { useBasePath } from '../../../../lib/use-base-path';
import { previewImageUrl } from '../../../../lib/artifact-image';
import type { WorkspaceFile } from '../../../../lib/workspace-files';
import { FileThumb } from '../../FileThumb';

// A chat attachment drawn with the SAME graphic as everywhere else a file appears — `FileThumb`, which the
// Files list, the "Agents at work" strip card and the Apps & files card all render. It states the rule
// itself: a file that previews itself on one screen has to preview itself on the others. The chip was the
// one surface that didn't, so it drew a flat text badge ("PDF"/"TXT"/"FILE") tinted the same accent colour
// whatever the file was, and rendered an image attachment as a bare <img> that broke to a torn-page glyph
// whenever the content wasn't a plain URL.
//
// Nothing new is invented here: the per-type artwork is the existing `/file-types/*.png` set, so a PDF in
// chat is the same red page it is in the gallery.

/** The `WorkspaceFile` view of an attachment: what the shared file helpers read. `content` is the RESOLVED
 *  body (a URL, a FileRecord header + URL, or an inline document), never the raw attachment. */
export function attachmentAsFile(att: MessageAttachment, content: string): WorkspaceFile {
  return {
    name: att.fileName ?? '',
    isApp: false,
    content,
    contentType: att.mimeType,
    // `WorkspaceFile.sizeBytes` is the int64-as-string the wire carries; the attachment's own `size` is
    // already a number, so it goes back to a string rather than being read as one.
    ...(att.size ? { sizeBytes: String(att.size) } : {}),
  };
}

/** The image an attachment previews as, or null when it isn't one.
 *
 *  Resolve through `previewImageUrl`, never the resolved content directly: a promoted or reference-backed
 *  file's content is the `<!--sym-file:…-->` header FOLLOWED by the URL, so handing it straight to an
 *  `<img src>` renders a broken image. This is the same extraction the doc viewer runs. */
export function attachmentImageUrl(att: MessageAttachment, content: string): string | null {
  return previewImageUrl(attachmentAsFile(att, content)) ?? (att.thumbnailUrl || att.url || null);
}

/** The file's graphic at `height`: its own picture when it is an image, else its file-type artwork. */
export function AttachmentGraphic({ att, content, height }: { att: MessageAttachment; content: string; height: number }) {
  const basePath = useBasePath();
  return <FileThumb item={attachmentAsFile(att, content)} basePath={basePath} height={height} />;
}
